// src/components/EditableImportsTable.js
import React, { useState, useMemo } from 'react';
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip, Paper, TextField, TablePagination, Stack } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';

// Helper function to format date
const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp.seconds * 1000).toLocaleString('vi-VN');
};

function EditableImportsTable({ imports, onEdit, onDelete, canEdit, canDelete }) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');

    const processedImports = useMemo(() => {
        return (imports || []).filter(item => {
            const term = searchTerm.toLowerCase();
            return (
                item.poNumber?.toLowerCase().includes(term) ||
                item.supplierName?.toLowerCase().includes(term) ||
                item.createdBy?.toLowerCase().includes(term) ||
                item.approvedBy?.toLowerCase().includes(term)
            );
        });
    }, [imports, searchTerm]);

    const handleExportExcel = () => {
        if (!processedImports || processedImports.length === 0) {
            alert("Không có dữ liệu để xuất.");
            return;
        }
        const dataToExport = processedImports.map(item => ({
            "Ngày Nhập": formatDateTime(item.importDate),
            "Mã PO": item.poNumber,
            "Nhà Cung Cấp": item.supplierName,
            "Người tạo": item.createdBy,
            "Người duyệt": item.approvedBy || 'Chưa duyệt',
        }));

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "PhieuNhapDaDuyet");
        XLSX.writeFile(wb, `PhieuNhapDaDuyet_${new Date().toLocaleDateString('vi-VN').replace(/\//g, '-')}.xlsx`);
    };
    
    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box>
            <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction="row" spacing={2} justifyContent="space-between">
                    <TextField 
                        label="Tìm kiếm theo Mã PO, NCC, Người tạo/duyệt..." 
                        variant="outlined" 
                        size="small" 
                        fullWidth
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportExcel}
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        Xuất Excel
                    </Button>
                </Stack>
            </Paper>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Ngày Nhập</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Mã PO</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Nhà Cung Cấp</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Người tạo</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Người duyệt</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Thao tác</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {processedImports.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(item => (
                            <TableRow key={item.id} hover>
                                <TableCell>{formatDateTime(item.importDate)}</TableCell>
                                <TableCell>{item.poNumber}</TableCell>
                                <TableCell>{item.supplierName}</TableCell>
                                <TableCell>{item.createdBy}</TableCell>
                                <TableCell>{item.approvedBy}</TableCell>
                                <TableCell align="right">
                                    {canEdit && (
                                        <Tooltip title="Chỉnh sửa">
                                            <IconButton onClick={() => onEdit(item)} color="warning" size="small">
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {canDelete && (
                                        <Tooltip title="Xóa vĩnh viễn">
                                            <IconButton onClick={() => onDelete(item)} color="error" size="small">
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={processedImports.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Box>
    );
}

export default EditableImportsTable;
